/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import themes, { styled } from '~/themes';
import { useCallback, useMemo, useState } from 'react';
import { fromQueryString, omitFromQueryString } from '~/utils/queryString';
import { DrawerTabs } from '~/components/common/Tabs';
import {
  STORES_DRAWER_TABS,
  STORES_DRAWER_TAB_VALUE,
} from '~/constants/stores';
import { useDeleteConnectionMutation } from '~/services/api/userManagement';
import { alertParams, showAlert } from '~/components/common/Alert';
import { IStore } from '~/models/stores';
import Tutorial from '~/components/common/Tutorial';
import { TutorialData } from '~/constants/tutorial';
import { useAuth } from '~/providers/AuthProvider';
import { useKeyDow } from '~/hooks/useKeyDown';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { Icon } from '~/components/common/Icon';
import { ElementSelect } from '~/components/common/ElementSelect/ElementSelect';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { General } from './components/General/General';
import { Teams } from './components/Teams';
import { DeliveryHours } from './components/DeliveryHours';
import { DeliveryHours as LateNightHours } from './components/LateNightHours';
import { ZoneAndFree } from './components/ZoneAndFree';
import { TaskSettings } from './components/TaskSetting';
import { OrderLimit } from './components/OrderLimit';
import NotifyCustomer from './components/Notify';

const StyledIntegrationListContainer = styled(Stack)(() => ({
  'position': 'relative',
  'height': '100%',
  'flexDirection': 'column',
  'boxSizing': 'border-box',
  '.tab-content:has(.save-change-btn)': {
    marginBottom: 70,
  },
  '.save-change-btn': {
    'position': 'absolute',
    'bottom': 0,
    'left': 0,
    'right': 0,
    'borderRadius': 0,
    'minHeight': 50,
    '&.loading': {
      opacity: 1,
      background: '#FED1C7 !important',
    },
  },
}));

const StyledLoading = styled('div')(() => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9,
  background: 'rgba(256, 256, 256, 0.7)',
}));

type StoreDetailProps = {
  storeData: IStore;
  isFetching?: boolean;
};

export const StoreDetail: React.FC<StoreDetailProps> = ({
  storeData,
  isFetching,
}) => {
  const onLateNightHours = useFeatureIsOn('cf-1828-config-late-night-hours');
  const location = useLocation();
  const navigate = useNavigate();
  const search = fromQueryString(location.search);
  const { org, isBundlePlan } = usePaymentInfo();

  const id = search.id as string;
  const [tab, setTab] = useState(STORES_DRAWER_TAB_VALUE.General);
  const { account, getTooltipOpen, updateUserTooltip } = useAuth();
  const hideDeleteStore = org?.bs_org_id && isBundlePlan;

  const tabs = useMemo(() => STORES_DRAWER_TABS, [search.type, storeData]);

  const handleCloseStoreDetail = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: omitFromQueryString(location.search, ['id', 'platform']),
    });
  }, [location]);

  useKeyDow({
    targetKey: 'Escape',
    disabled: !id,
    callback: handleCloseStoreDetail,
  });

  const { mutate: deleteConnectionMutation } = useDeleteConnectionMutation({
    onSuccess: () => {
      handleCloseStoreDetail();
    },
  });

  const handleChangeTab = useCallback((value: string) => {
    setTab(value);
  }, []);

  const renderTabComponent = useMemo(
    () => (
      <Tutorial
        isOpen={getTooltipOpen(TutorialData.OptimizeStore.id)}
        onClose={() =>
          updateUserTooltip({
            [TutorialData.OptimizeStore.id]: true,
          })
        }
        delay={300}
        {...TutorialData.OptimizeStore}
      >
        <DrawerTabs
          tabs={tabs}
          value={tab}
          onChange={handleChangeTab}
          lowerCaseLabel
          variant='scrollable'
          containerClassName='tab-container'
        />
      </Tutorial>
    ),
    [tab, tabs, handleChangeTab, id, account],
  );

  const renderTabContent = useMemo(() => {
    switch (tab) {
      case STORES_DRAWER_TAB_VALUE.Teams:
        return <Teams data={storeData} />;
      case STORES_DRAWER_TAB_VALUE.DeliveryHours:
        return onLateNightHours ? (
          <LateNightHours data={storeData} />
        ) : (
          <DeliveryHours data={storeData} />
        );
      case STORES_DRAWER_TAB_VALUE.TaskSettings:
        return <TaskSettings data={storeData} />;
      case STORES_DRAWER_TAB_VALUE.DeliveryZoneFee:
        return <ZoneAndFree data={storeData} />;
      case STORES_DRAWER_TAB_VALUE.OrderLimit:
        return <OrderLimit data={storeData} />;
      case STORES_DRAWER_TAB_VALUE.Notify:
        return <NotifyCustomer data={storeData} />;
      case STORES_DRAWER_TAB_VALUE.General:
      default:
        return <General storeData={storeData} />;
    }
  }, [tab, storeData, isFetching]);

  return (
    <StyledIntegrationListContainer data-testid='store-detail'>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        p={2}
        pb={1}
      >
        <Stack mr='auto' color={themes.color.violet900}>
          <Typography fontSize={18} fontWeight='bold' data-testid='store-name'>
            {storeData.display_name}
          </Typography>
        </Stack>
        <Stack direction='row' spacing={0.6} maxHeight={themes.spacing(3)}>
          {!hideDeleteStore && (
            <ElementSelect
              paperProps={{
                sx: {
                  'width': 145,
                  'borderRadius': '10px',
                  '.MuiButtonBase-root': {
                    padding: '6px',
                  },
                },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              elementSelect={() => (
                <MoreHorizIcon
                  sx={{ fontSize: 20, color: themes.color.violet900 }}
                />
              )}
              iconButtonProps={{
                sx: { height: 26, width: 26 },
              }}
              onChange={(o) => {
                if (o.value === 'delete') {
                  showAlert({
                    ...alertParams.warning,
                    description:
                      'You are about to delete this store. This will affect \nexisting store integrations. Are you sure you want to continue?',
                    okText: 'Yes, Delete',
                    cancelText: 'Cancel',
                    onOk: () => {
                      deleteConnectionMutation(id as string);
                    },
                  });
                }
              }}
              options={[
                {
                  label: 'Delete',
                  value: 'delete',
                },
              ]}
            />
          )}
          <IconButton onClick={() => handleCloseStoreDetail()}>
            <Icon name='close' size={10} color={themes.bg.darkPurple} />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        sx={{
          '& .MuiTab-root': {
            px: '0px !important',
          },
          '& .tab-container': {
            gap: 2.4,
            px: 2,
          },
          [themes.breakpoints.down('md')]: {
            '& .tab-container': {
              width: 'max-content',
              minWidth: '100%',
            },
          },
        }}
      >
        {renderTabComponent}
      </Box>

      <Box
        px={2}
        py={1}
        pb={0}
        mb={2}
        className='customized-scrollbar tab-content'
        sx={{
          overflow: 'auto',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          height: '100%',
        }}
      >
        {isFetching && (
          <StyledLoading>
            <CircularProgress size={30} />
          </StyledLoading>
        )}
        {renderTabContent}
      </Box>
    </StyledIntegrationListContainer>
  );
};
